<template>
  <div class="order-page">
    <!-- 頁面標題 -->
    <header class="header">
      <!-- 返回箭頭：只在第一步顯示 -->
      <div class="tooltip-container" v-if="currentStep === 1">
        <img
          src="@/assets/picture/back.png"
          alt="Back to Booking"
          class="back-arrow"
          @click="goToBooking"
        />
        <span class="tooltip-text">回到訂購頁面</span>
      </div>

      <h1 v-if="currentStep === 1">第一步 - 確認商品</h1>
      <h1 v-if="currentStep === 2">第二步 - 選擇付款方式</h1>
      <h1 v-if="currentStep === 3">完成訂房</h1>
    </header>

    <!-- 進度條 -->
    <div class="progress-bar">
      <div :class="['step', currentStep >= 1 ? 'active' : '']">
        <div class="circle">1</div>
        <span class="label">訂購明細</span>
      </div>
      <div class="separator">&gt;</div>
      <div :class="['step', currentStep >= 2 ? 'active' : '']">
        <div class="circle">2</div>
        <span class="label">付款方式</span>
      </div>
      <div class="separator">&gt;</div>
      <div :class="['step', currentStep >= 3 ? 'active' : '']">
        <div class="circle">3</div>
        <span class="label">完成訂房</span>
      </div>
    </div>

    <!-- 主內容區 -->
    <section class="content-section">
      <!-- ✅ 空購物車提示 -->
      <div v-if="showEmptyCartMessage" class="empty-cart-message">
        <div class="empty-cart-icon">🛒</div>
        <h2>購物車目前為空</h2>
        <p>您還沒有添加任何房間到購物車中</p>
        <button class="go-booking-btn" @click="goToBooking">前往訂房</button>
      </div>

      <!-- 第一頁：訂購明細 -->
      <div
        v-if="currentStep === 1 && !showEmptyCartMessage"
        class="order-details"
      >
        <h2>訂購明細</h2>

        <!-- ✅ 加載狀態提示 -->
        <div v-if="isLoading" class="loading-message">
          <p>正在加載購物車資料...</p>
        </div>

        <!-- ✅ 只有當有訂單且不在加載時才顯示表格 -->
        <div v-else-if="orders && orders.length > 0">
          <div class="desktop-table-container">
            <table class="order-table desktop-table">
              <thead>
                <tr>
                  <th>房間資訊</th>
                  <th>單價</th>
                  <th>天數</th>
                  <th>總價</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(order, index) in orders" :key="order.cartDetailId">
                  <td>
                    <!-- ✅ 增加房間號碼顯示 -->
                    {{ order.roomType }} - {{ order.roomNumber }}<br />
                    入住日期：{{ order.checkInDate }}<br />
                    退房日期：{{ order.checkOutDate }}<br />
                    {{ order.nights }} 晚，{{ order.package }}
                  </td>
                  <td>NT$ {{ order.unitPrice.toLocaleString() }}</td>
                  <td>{{ order.nights }}</td>
                  <td>NT$ {{ order.totalPrice.toLocaleString() }}</td>
                  <td style="text-align: right; width: 10px">
                    <!-- ✅ 修正：使用 cartDetailId 而不是 id -->
                    <button
                      @click="removeOrder(index, order.cartDetailId)"
                      class="remove-btn"
                    >
                      移除
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- 手機版卡片式佈局 -->
          <div class="mobile-cards">
            <div
              v-for="(order, index) in orders"
              :key="'card-' + order.cartDetailId"
              class="order-card"
            >
              <div class="order-card-header">
                <div class="room-info">
                  <div class="room-title">
                    {{ order.roomType }} - {{ order.roomNumber }}
                  </div>
                  <div class="room-details">
                    入住：{{ order.checkInDate }} | 退房：{{ order.checkOutDate
                    }}<br />
                    {{ order.nights }} 晚，{{ order.package }}
                  </div>
                </div>
                <button
                  @click="removeOrder(index, order.cartDetailId)"
                  class="remove-btn"
                >
                  移除
                </button>
              </div>
              <div class="order-card-body">
                <div class="info-item">
                  <div class="info-label">單價</div>
                  <div class="info-value">
                    NT$ {{ order.unitPrice.toLocaleString() }}
                  </div>
                </div>
                <div class="info-item">
                  <div class="info-label">天數</div>
                  <div class="info-value">{{ order.nights }}</div>
                </div>
                <div class="info-item">
                  <div class="info-label">總價</div>
                  <div class="info-value">
                    NT$ {{ order.totalPrice.toLocaleString() }}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- 訂單摘要 -->
          <div class="order-summary">
            <h2>訂單摘要</h2>
            <div class="summary-item">
              <span>訂單金額：</span>
              <span>NT$ {{ orderSummary.totalAmount.toLocaleString() }}</span>
            </div>
            <div class="summary-item">
              <span>服務費：</span>
              <span>NT$ {{ orderSummary.serviceFee.toLocaleString() }}</span>
            </div>
            <div class="summary-total">
              <span>總金額：</span>
              <span>NT$ {{ orderSummary.grandTotal.toLocaleString() }}</span>
            </div>
          </div>
        </div>
      </div>

      <div v-if="currentStep === 2" class="payment-methods">
        <!-- 訂購明細 -->
        <h2 class="section-title">訂購明細</h2>
        <div class="desktop-table-container">
          <table class="order-table desktop-table">
            <thead>
              <tr>
                <th>房間資訊</th>
                <th>單價</th>
                <th>天數</th>
                <th>總價</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="order in orders" :key="order.cartDetailId">
                <td>
                  <!-- ✅ 增加房間號碼顯示 -->
                  {{ order.roomType }} - {{ order.roomNumber }}<br />
                  入住日期：{{ order.checkInDate }}<br />
                  退房日期：{{ order.checkOutDate }}<br />
                  {{ order.nights }} 晚，{{ order.package }}
                </td>
                <td>NT$ {{ order.unitPrice.toLocaleString() }}</td>
                <td>{{ order.nights }}</td>
                <td>NT$ {{ order.totalPrice.toLocaleString() }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- 手機版卡片式佈局 -->
        <div class="mobile-cards">
          <div
            v-for="order in orders"
            :key="'step2-card-' + order.cartDetailId"
            class="order-card"
          >
            <div class="order-card-header">
              <div class="room-info">
                <div class="room-title">
                  {{ order.roomType }} - {{ order.roomNumber }}
                </div>
                <div class="room-details">
                  入住：{{ order.checkInDate }} | 退房：{{ order.checkOutDate
                  }}<br />
                  {{ order.nights }} 晚，{{ order.package }}
                </div>
              </div>
            </div>
            <div class="order-card-body">
              <div class="info-item">
                <div class="info-label">單價</div>
                <div class="info-value">
                  NT$ {{ order.unitPrice.toLocaleString() }}
                </div>
              </div>
              <div class="info-item">
                <div class="info-label">天數</div>
                <div class="info-value">{{ order.nights }}</div>
              </div>
              <div class="info-item">
                <div class="info-label">總價</div>
                <div class="info-value">
                  NT$ {{ order.totalPrice.toLocaleString() }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 訂單摘要 -->
        <div class="order-summary">
          <h2>訂單摘要</h2>
          <div class="summary-item">
            <span>訂單金額：</span>
            <span>NT$ {{ orderSummary.totalAmount.toLocaleString() }}</span>
          </div>
          <div class="summary-item">
            <span>服務費：</span>
            <span>NT$ {{ orderSummary.serviceFee.toLocaleString() }}</span>
          </div>
          <div class="summary-total">
            <span>總金額：</span>
            <span>NT$ {{ orderSummary.grandTotal.toLocaleString() }}</span>
          </div>
        </div>

        <!-- 訂購人聯絡資訊 -->
        <div class="contact-note-wrapper">
          <div class="form-box">
            <h2 class="section-title">聯絡資訊</h2>
            <p class="info-note">請您填寫以下資料，「*」為必填欄位。</p>
            <form class="form-container">
              <div class="form-group">
                <label for="lastName">* 姓名</label>
                <input
                  type="text"
                  id="lastName"
                  v-model="buyerInfo.lastName"
                  placeholder="請輸入姓名"
                  required
                />
              </div>

              <div class="form-group">
                <label>* 性別</label>
                <div class="radio-group">
                  <label>
                    <input
                      type="radio"
                      value="male"
                      v-model="buyerInfo.gender"
                    />
                    先生
                  </label>
                  <label>
                    <input
                      type="radio"
                      value="female"
                      v-model="buyerInfo.gender"
                    />
                    女士
                  </label>
                </div>
              </div>

              <div class="form-group">
                <label for="email">* 電子信箱</label>
                <input
                  type="email"
                  id="email"
                  v-model="buyerInfo.email"
                  placeholder="請輸入電子信箱"
                  required
                />
              </div>

              <div class="form-group">
                <label for="phone">* 手機號碼</label>
                <input
                  type="text"
                  id="phone"
                  v-model="buyerInfo.phone"
                  placeholder="請輸入手機號碼"
                  required
                />
              </div>
            </form>
          </div>

          <!-- 訂房備註 -->
          <div class="remark-area">
            <h2 class="section-title long-title">訂房備註</h2>
            <textarea
              v-model="remarks"
              placeholder="請輸入備註...(150字內)"
              maxlength="300"
            ></textarea>
          </div>
        </div>

        <div class="payment-terms-wrapper">
          <!-- 付款方式 -->
          <div class="payment-box">
            <h2 class="section-title short-title">付款方式</h2>
            <div class="payment-option">
              <label>
                <input type="radio" value="現金" v-model="paymentMethod" />
                現金(現場付款)
              </label>
            </div>
          </div>

          <!-- 條款確認 -->
          <div class="terms">
            <label>
              <!-- ✅ 只需要 v-model，移除 @change 事件 -->
              <input type="checkbox" v-model="termsAccepted" />
              <!-- ✅ 保留點擊文字的功能 -->
              <span
                class="terms-text"
                @click="handleTermsClick"
                style="
                  cursor: pointer;
                  text-decoration: underline;
                  color: #007bff;
                "
              >
                我已閱讀並同意預訂條款
              </span>
            </label>
          </div>
        </div>
      </div>

      <div v-if="currentStep === 3" class="completion-screen">
        <div class="checkmark-container">
          <svg
            class="checkmark"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 52 52"
          >
            <!-- 圓形 -->
            <circle
              class="checkmark-circle"
              cx="26"
              cy="26"
              r="25"
              fill="none"
            />
            <!-- 打勾符號 -->
            <path class="checkmark-check" fill="none" d="M14 27l10 10 14-20" />
          </svg>
        </div>
        <h2 class="completion-title">訂房完成！</h2>
        <p class="completion-message">感謝您的預訂，我們期待您的光臨！</p>
        <button class="back-home-btn" @click="goToHome">返回首頁</button>
      </div>
    </section>

    <!-- 操作按鈕 -->
    <footer class="actions" v-if="!showEmptyCartMessage">
      <!-- 上一步按鈕 -->
      <button
        v-if="currentStep > 1 && currentStep < 3"
        class="back-btn"
        @click="goBack"
      >
        上一步
      </button>

      <!-- 下一步按鈕 -->
      <button v-if="currentStep < 3" class="next-btn" @click="goNext">
        下一步
      </button>
    </footer>
  </div>
</template>

<script>
import orderLogic from "@/assets/js/order.js"; // 引用外部邏輯

export default {
  name: "OrderPage",
  mixins: [orderLogic], // 混入邏輯
  created() {
    this.fetchOrders(); // 在組件創建時調用 fetchOrders 方法
  },
};
</script>

<style src="@/assets/css/order.css"></style>
<!-- 匯入獨立的 CSS 檔案 -->
